import { Fragment, useState } from "react";
import "./RoleListTable.css";
import ComparisonChart from "./ComparisonChart";
import JobSpecPanel from "./JobSpecPanel";

/**
 * RoleListTable
 *
 * Role-list table shown when a Core Vertical is selected and no role/Job
 * Title picked yet. Ported from core-compensation.component.ts/html, which
 * (despite the "core" name - unrelated to the "Core Stack" sub-stack
 * concept) is old Angular's actual role-LIST/landing page: it's swapped in
 * whenever `hasStacks` (Vertical+SubStack+JobTitle all resolved) is false,
 * exactly matching when this component is shown in App.js
 * (`filters.verticalStack && !activeJobTitleId`) - confirmed via
 * recruitment-component.component.html's `@if(!hasStacks)`.
 *
 * Two view modes, both real old behavior:
 *  - No territory selected (default): one flat £-amount column per
 *    territory (UK/Ireland/South Africa/Romania/India/Brazil), each the
 *    single blended full_l2 pivot figure. Unchanged from before this file
 *    was extended - this was already a correct port.
 *  - 1-2 territories selected: table regroups into per-territory NTR/Exp/
 *    Vexp column blocks (flag + code header spanning 3 sub-columns),
 *    sourced from each row's item_data (added to compensation_pivot.sql/
 *    routes.py 2026-08-05 - previously missing entirely, so this view had
 *    no data to render). Bold + tooltip on synthetic/zero-employee-count
 *    cells ported from getLinkClass()/getSyntheticTooltip() in
 *    core-compensation.component.ts.
 *
 * FIXED 2026-08-05: territoryIds is now a PROP (from FilterBar.jsx's own
 * Territory control, App.js's filters.territoryIds), not an internal
 * selector owned by this component. Old Angular really does have two
 * separate territory controls (core-compensation.component.ts's own
 * `selectedTerritories`/max 2, vs non-core-compensation.component.ts's own
 * `territoryIds`/unlimited) - but they're the SAME visual slot (teal
 * "Time period + Territory" bar), swapped because only one page component
 * is ever mounted at a time. This port had wrongly built that as two
 * different, simultaneously-visible controls instead (this one buried in
 * the table, plus a second unrelated one always shown in FilterBar) - so
 * picking a territory in FilterBar (the one actually visible/reachable)
 * never touched this table. Consolidated into one control, living in
 * FilterBar.jsx, whose cap swaps between 2 (this page) and unlimited (job
 * detail) based on whether a Job Title is active - see FilterBar.jsx.
 *
 * Per-role expand chevron (`<ng-template #expandedrow>` in
 * core-compensation.component.html): clicking the chevron on the left of a
 * row expands it in place to show that role's Comparison Chart (historical
 * £ vs CPI, reusing ComparisonChart.jsx, filtered by whatever territoryIds
 * this table already has - matches old's own
 * `this.selectedTerritories.length > 0 ? historicalData.filter(...) : ...`)
 * plus the embedded Job Specification panel (JobSpecPanel.jsx). Separate
 * from clicking the row/role name itself, which still just calls
 * onRoleClick to drive the Cost Band Detail transition, same as before -
 * old keeps these two clicks on different elements for the same reason
 * (toggleRow(item) on the chevron `<td>`, highlightRole(item,$event) on the
 * role-name `<td>`).
 *
 * data: array of rows from GET /api/compensation/pivot, each row shaped like:
 *   { job_title_id, sub_stack_id, vertical_stack_id, role, job_level,
 *     source_job_title_alternative, UNITEDKINGDOM, IRELAND, SOUTHAFRICA,
 *     ROMANIA, INDIA, BRAZIL, item_data: [{ territory_id, territory_name,
 *     full_l1, full_l2, full_l3, employee_count, synthetic_data,
 *     is_blended }, ...] }
 *
 * onRoleClick(row): called when a role row is clicked (drives Clear Core
 * Stack / Cost Band Detail transition in FilterBar.jsx)
 *
 * highlightedRoleId: ported from old Angular's highlightRole() - the first
 * row is bold by default the instant data loads (and again whenever the
 * data is re-filtered, e.g. by picking a Sub Stack), purely as a visual
 * affordance. Unlike the old code, this does NOT also emit a navigation
 * signal - our App.js keeps the Role table and the Cost Band Detail view as
 * separate pages (see the note on activeJobTitleId there), so bolding a row
 * here never hides the table. App.js computes this as roleListData[0]'s id.
 */

const TERRITORY_COLUMNS = [
  { key: "UNITEDKINGDOM", label: "UK", flag: "\u{1F1EC}\u{1F1E7}" },
  { key: "IRELAND", label: "Ireland", flag: "\u{1F1EE}\u{1F1EA}" },
  { key: "SOUTHAFRICA", label: "South Africa", flag: "\u{1F1FF}\u{1F1E6}" },
  { key: "ROMANIA", label: "Romania", flag: "\u{1F1F7}\u{1F1F4}" },
  { key: "INDIA", label: "India", flag: "\u{1F1EE}\u{1F1F3}" },
  { key: "BRAZIL", label: "Brazil", flag: "\u{1F1E7}\u{1F1F7}" },
];

// territory_id -> short code + flag emoji, for the grouped-column headers.
// Old Angular used real flag image assets (assets/images/flags/*.png) we
// don't have ported - the flat/default view above already substitutes
// Unicode flag emojis for the same territories, so the grouped view reuses
// that same substitution for visual consistency.
const TERRITORY_META = {
  4: { code: "UK", flag: "\u{1F1EC}\u{1F1E7}" },
  5: { code: "IRE", flag: "\u{1F1EE}\u{1F1EA}" },
  2: { code: "SA", flag: "\u{1F1FF}\u{1F1E6}" },
  3: { code: "RO", flag: "\u{1F1F7}\u{1F1F4}" },
  1: { code: "IND", flag: "\u{1F1EE}\u{1F1F3}" },
  6: { code: "BRA", flag: "\u{1F1E7}\u{1F1F7}" },
};

// Ported from core-compensation.component.ts's sortOrder (same constant
// used identically across every other chart/table in this app) - fixed
// display order by territory_id.
const TERRITORY_SORT_ORDER = [4, 5, 2, 3, 1, 6];

function formatAmount(value) {
  if (value === null || value === undefined) return "-";
  return `£${Math.round(value).toLocaleString()}`;
}

function getTerritoryDataById(row, territoryId) {
  return (row.item_data || []).find((t) => t.territory_id === territoryId);
}

function isSyntheticField(syntheticData, identifier) {
  if (!syntheticData || syntheticData.length === 0) return false;
  return syntheticData.some((s) => s.toLowerCase().includes(identifier));
}

// Ported from getSyntheticTooltip() - strips the leading "l1"/"l2"/"l3"
// label off the matching synthetic_data entry, leaving just the reason
// text (e.g. "l1 Linear Percentage Data" -> "Linear Percentage Data").
function syntheticTooltip(syntheticData, identifier) {
  if (!syntheticData) return "";
  const match = syntheticData.find((s) => s.toLowerCase().includes(identifier));
  if (!match) return "";
  return match.replace(new RegExp(`^\\s*${identifier}\\s*`, "i"), "");
}

// Ported from getLinkClass() - bold + tooltip is a synthetic-field marker
// for non-blended territories, or a zero-employee-count marker for
// blended ones. `identifier` is 'l1'/'l2'/'l3'.
function cellMeta(itemData, identifier) {
  if (!itemData) return { bold: false, title: "" };
  if (itemData.is_blended === false) {
    const synthetic = isSyntheticField(itemData.synthetic_data, identifier);
    return { bold: synthetic, title: synthetic ? `Synthetic Data: ${syntheticTooltip(itemData.synthetic_data, identifier)}` : "" };
  }
  return { bold: !itemData.employee_count, title: "" };
}

export default function RoleListTable({ data, onRoleClick, highlightedRoleId, territoryIds }) {
  const [expandedRoleId, setExpandedRoleId] = useState(null);

  if (!data || data.length === 0) return null;

  const groupedTerritoryIds = [...(territoryIds || [])].sort(
    (a, b) => TERRITORY_SORT_ORDER.indexOf(a) - TERRITORY_SORT_ORDER.indexOf(b)
  );
  const isGrouped = groupedTerritoryIds.length > 0;
  // +1 for the chevron column, +1 for the role-name column.
  const totalCols = 2 + (isGrouped ? groupedTerritoryIds.length * 3 : TERRITORY_COLUMNS.length);

  const toggleExpanded = (jobTitleId) => {
    setExpandedRoleId((prev) => (prev === jobTitleId ? null : jobTitleId));
  };

  return (
    <div className="role-list-wrap">
      {/* NO separate heading here - CompensationHeader above already carries
          the "Fully inclusive annual compensation cost" text. Table starts
          directly, matching old Minerva's single-heading behavior. */}
      <div className="role-list-table-card">
        <table className="role-list-table">
          <thead>
            {isGrouped ? (
              <>
                <tr>
                  <th className="role-list-th role-list-th-expand" rowSpan={2}></th>
                  <th className="role-list-th role-list-th-role" rowSpan={2}></th>
                  {groupedTerritoryIds.map((tid, idx) => {
                    const meta = TERRITORY_META[tid] || {};
                    return (
                      <th key={tid} colSpan={3} className={`role-list-th role-list-th-group${idx % 2 !== 0 ? " role-list-th-shaded" : ""}`}>
                        <span className="role-list-th-flag">{meta.flag}</span> {meta.code || tid}
                      </th>
                    );
                  })}
                </tr>
                <tr>
                  {groupedTerritoryIds.map((tid, idx) => (
                    <Fragment key={tid}>
                      <th className={`role-list-th${idx % 2 !== 0 ? " role-list-th-shaded" : ""}`}>NTR</th>
                      <th className={`role-list-th${idx % 2 !== 0 ? " role-list-th-shaded" : ""}`}>Exp</th>
                      <th className={`role-list-th${idx % 2 !== 0 ? " role-list-th-shaded" : ""}`}>Vexp</th>
                    </Fragment>
                  ))}
                </tr>
              </>
            ) : (
              <tr>
                <th className="role-list-th role-list-th-expand"></th>
                <th className="role-list-th role-list-th-role">Role</th>
                {TERRITORY_COLUMNS.map((col) => (
                  <th key={col.key} className="role-list-th">
                    <span className="role-list-th-flag">{col.flag}</span> {col.label}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {data.map((row) => {
              const isExpanded = expandedRoleId === row.job_title_id;
              return (
                <Fragment key={row.job_title_id}>
                  <tr
                    className={`role-list-row${row.job_title_id === highlightedRoleId ? " role-list-row-highlighted" : ""}`}
                    onClick={() => onRoleClick?.(row)}
                  >
                    <td className="role-list-td role-list-td-expand">
                      <button
                        type="button"
                        className="role-list-expand-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(row.job_title_id);
                        }}
                        title={isExpanded ? "Hide comparison chart & job specification" : "Show comparison chart & job specification"}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded ? "▾" : "▸"}
                      </button>
                    </td>
                    <td className="role-list-td role-list-td-role">{row.role}</td>
                    {isGrouped
                      ? groupedTerritoryIds.map((tid, idx) => {
                          const itemData = getTerritoryDataById(row, tid);
                          const shaded = idx % 2 !== 0;
                          return (
                            <Fragment key={tid}>
                              {["l1", "l2", "l3"].map((identifier) => {
                                const value = itemData ? { l1: itemData.full_l1, l2: itemData.full_l2, l3: itemData.full_l3 }[identifier] : null;
                                const meta = cellMeta(itemData, identifier);
                                return (
                                  <td
                                    key={`${tid}-${identifier}`}
                                    className={`role-list-td${shaded ? " role-list-td-shaded" : ""}${meta.bold ? " role-list-td-bold" : ""}`}
                                    title={meta.title || undefined}
                                  >
                                    {formatAmount(value)}
                                  </td>
                                );
                              })}
                            </Fragment>
                          );
                        })
                      : TERRITORY_COLUMNS.map((col) => (
                          <td key={col.key} className="role-list-td">
                            {formatAmount(row[col.key])}
                          </td>
                        ))}
                  </tr>
                  {isExpanded && (
                    <tr className="role-list-expand-row">
                      <td colSpan={totalCols} className="role-list-expand-cell">
                        <div className="role-list-expand-title">Comparison Chart</div>
                        <ComparisonChart
                          filters={{ jobTitle: { id: row.job_title_id }, territoryIds: territoryIds || [] }}
                        />
                        <JobSpecPanel jobTitleId={row.job_title_id} verticalStackId={row.vertical_stack_id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
